import { z } from "zod"
import {
  branchIdSchema,
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const requiredText = (maximum: number) => z.string().trim().min(1).max(maximum)
const optionalText = (maximum: number) => z.string().trim().min(1).max(maximum).nullable()

export const internalCustomerInputV1Schema = z.object({
  customerId: entityIdSchema,
  contactId: entityIdSchema,
  serviceLocationId: entityIdSchema,
  billingProfileId: entityIdSchema,
  customerType: z.enum(["residential", "commercial", "property_manager"]),
  displayName: requiredText(160),
  contactName: requiredText(160),
  email: z.string().trim().email().max(254).nullable(),
  phone: optionalText(32),
  emailConsent: z.boolean(),
  smsConsent: z.boolean(),
  addressLine1: requiredText(240),
  addressLine2: optionalText(240),
  city: requiredText(120),
  region: requiredText(120),
  postalCode: requiredText(24),
  country: z.string().trim().length(2).transform((value) => value.toUpperCase()),
  timezone: requiredText(64),
  currency: z.string().regex(/^[A-Z]{3}$/),
  desiredStatus: z.enum(["Draft", "Active"]),
}).strict().superRefine((customer, refinement) => {
  if (customer.email === null && customer.phone === null) {
    refinement.addIssue({ code: "custom", message: "A contact channel is required." })
  }
  const identifiers = [
    customer.customerId,
    customer.contactId,
    customer.serviceLocationId,
    customer.billingProfileId,
  ]
  if (new Set(identifiers).size !== identifiers.length) {
    refinement.addIssue({ code: "custom", message: "Aggregate identifiers must differ." })
  }
})

const baseRequest = z.object({
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
  branchId: branchIdSchema,
  legalEntityId: legalEntityIdSchema,
  customer: internalCustomerInputV1Schema,
}).strict()

export const internalCustomerDuplicatePreviewRequestV1Schema = baseRequest

export const internalCustomerDuplicatePreviewResponseV1Schema = z.object({
  responseVersion: z.literal(1),
  status: z.enum(["reviewed", "denied", "retry"]),
  correlationId: correlationIdSchema,
  candidateCustomerIds: z.array(entityIdSchema).max(25),
  duplicateReviewFingerprint: z.string().regex(/^[a-f0-9]{64}$/).nullable(),
}).strict()

export const internalCustomerCreateRequestV1Schema = baseRequest.extend({
  duplicateReviewFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  duplicateDecision: z.enum(["no_candidates", "create_new"]),
}).strict()

export const internalCustomerCreateResponseV1Schema = z.object({
  responseVersion: z.literal(1),
  status: z.enum(["confirmed", "conflict", "denied", "retry"]),
  correlationId: correlationIdSchema,
  customerId: entityIdSchema.nullable(),
  state: z.enum(["Draft", "Active"]).nullable(),
  version: z.number().int().positive().nullable(),
  duplicateCandidateCount: z.number().int().nonnegative().nullable(),
}).strict()

export type InternalCustomerInputV1 = z.infer<typeof internalCustomerInputV1Schema>
export type InternalCustomerDuplicatePreviewRequestV1 = z.infer<
  typeof internalCustomerDuplicatePreviewRequestV1Schema
>
export type InternalCustomerDuplicatePreviewResponseV1 = z.infer<
  typeof internalCustomerDuplicatePreviewResponseV1Schema
>
export type InternalCustomerCreateRequestV1 = z.infer<
  typeof internalCustomerCreateRequestV1Schema
>
export type InternalCustomerCreateResponseV1 = z.infer<
  typeof internalCustomerCreateResponseV1Schema
>
